import React, { useCallback, useMemo, useState } from 'react'
import { Button, Checkbox } from 'antd';
import Tooltip from '../../common/Tooltip';
import { EditorState } from 'draft-js'
import { useTranslation } from 'react-i18next'
import { decorator } from '../../RichEditor/utils'
import { useStore } from '../../../store/store'
import { ItemTypes } from '../../../constants/constants'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { icon } from '@fortawesome/fontawesome-svg-core/import.macro'
import { appendRawContents, rawContentHasEntity, rawContentToText } from '../../../utils/predefinedValues'

const PREVIEW_LENGTH = 60

const truncate = (text) =>
  text.length > PREVIEW_LENGTH ? `${text.slice(0, PREVIEW_LENGTH)}…` : text

// Valeurs prédéfinies proposées dans le résultat d'un déclencheur : ce que le
// déclencheur écrira dans la variable est la même chose que ce que
// l'utilisateur pourrait appliquer à la main depuis l'aperçu du formulaire.
const TriggerResultPredefinedValues = ({ box, result, record }) => {
  const { t } = useTranslation()
  const updateTrigger = useStore(({ updateTrigger }) => updateTrigger)
  const bumpDocumentContentSyncTick = useStore(({ bumpDocumentContentSyncTick }) => bumpDocumentContentSyncTick)
  // Les valeurs d'une variable texte custom vivent en base sur la variable,
  // celles d'un champ de texte et d'une image sur la box.
  const variablePredefinedValues = useStore(({ variables }) => variables[box.id]?.predefinedValues)
  const [selected, setSelected] = useState([])

  const isCustomText = box.type === ItemTypes.CUSTOM_TEXT_VARIABLE
  const isRichText = box.type === ItemTypes.TEXT_VARIABLE
  const isImage = box.type === ItemTypes.IMG_VARIABLE

  const options = useMemo(() => {
    if (isCustomText) {
      return (variablePredefinedValues ?? [])
        .map((value, index) => ({ value, index }))
        .filter(({ value }) => typeof value === 'string' && value.trim() !== '')
    }
    if (isRichText) {
      return (box.predefinedValues ?? [])
        .map((value, index) => ({ value, index }))
        .filter(({ value }) => typeof value === 'string' && (
          rawContentToText(value) !== '' || rawContentHasEntity(value, 'IMAGE')
        ))
    }
    if (isImage) {
      return (box.predefinedValues ?? [])
        .map((value, index) => ({ value, index }))
        .filter(({ value }) => !!value?.url)
    }
    return []
  }, [isCustomText, isRichText, isImage, variablePredefinedValues, box.predefinedValues])

  const applyToResult = useCallback((resultCallback) => {
    updateTrigger(record.id, box.id, { resultCallback })
  }, [record.id, box.id, updateTrigger])

  const handleApplyText = useCallback(() => {
    if (selected.length === 0) return
    const chosen = options.filter(({ index }) => selected.includes(index))

    if (isCustomText) {
      const addition = chosen.map(({ value }) => value).join('\n')
      applyToResult((r) => {
        const current = String(r.value ?? '')
        r.value = current.trim() === '' ? addition : `${current}\n${addition}`
      })
    } else {
      const rawValues = chosen.map(({ value }) => value)
      applyToResult((r) => {
        const nextEditorState = appendRawContents({
          editorState: r.content?.editorState,
          rawValues,
        })
        if (!nextEditorState) return
        // Recréé avec le décorateur : le contenu par défaut d'un résultat vient
        // d'un EditorState.createEmpty() qui n'en a pas, les variables
        // s'afficheraient alors en texte brut.
        // Objet neuf et pas une mutation : ce contenu par défaut est une
        // constante partagée par tous les déclencheurs.
        r.content = {
          ...r.content,
          editorState: EditorState.createWithContent(
            nextEditorState.getCurrentContent(),
            decorator
          ),
        }
      })
      // L'aperçu du résultat est un éditeur qui garde une copie locale de son
      // contenu tant qu'il a le focus : sans ce top, l'ajout n'y apparaîtrait
      // qu'en le quittant.
      bumpDocumentContentSyncTick()
    }
    setSelected([])
  }, [selected, options, isCustomText, applyToResult, bumpDocumentContentSyncTick])

  const handleApplyImage = useCallback((url) => {
    const imgEl = document.createElement('img')
    // Le ratio a besoin des dimensions réelles ; un échec de chargement ne doit
    // pas empêcher de poser la source.
    const apply = () => {
      applyToResult((r) => {
        r.content = {
          ...(r.content ?? box.content),
          src: url,
          ...(imgEl.width && imgEl.height ? { ratio: imgEl.width / imgEl.height } : {}),
        }
        // La largeur de la box est conservée, la hauteur suit le ratio.
        r.height = 'auto'
      })
    }
    imgEl.onload = apply
    imgEl.onerror = apply
    imgEl.src = url
  }, [applyToResult, box.content])

  if (options.length === 0) return null
  // Champ masqué : le déclencheur n'écrit plus rien dedans.
  if (result?.hideField) return null

  return (
    <div style={sectionStyle}>
      <div style={{ marginBottom: 8, fontWeight: 600, fontSize: 12 }}>{t('predefined-values')}</div>
      {isImage ? (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 8, maxHeight: 200, overflowY: 'auto' }}>
          {options.map(({ value }) => (
            <Tooltip key={value.url} title={value.filename} placement="top" mouseEnterDelay={0.3}>
              <button
                type="button"
                onClick={() => handleApplyImage(value.url)}
                style={{
                  ...thumbButtonStyle,
                  borderColor: result?.content?.src === value.url ? '#5499DE' : 'rgba(11,34,57,0.12)',
                }}
              >
                <img src={value.url} alt={value.filename} style={thumbImgStyle} />
              </button>
            </Tooltip>
          ))}
        </div>
      ) : (
        <>
          <div style={{ maxHeight: 160, overflowY: 'auto' }}>
            <Checkbox.Group
              value={selected}
              onChange={setSelected}
              style={{ display: 'flex', flexDirection: 'column', gap: 6 }}
              options={options.map(({ value, index }) => ({
                value: index,
                label: isCustomText
                  ? truncate(value)
                  : (
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                      {rawContentHasEntity(value, 'IMAGE') && (
                        <FontAwesomeIcon
                          icon={icon({ name: 'image', style: 'solid' })}
                          style={{ color: '#5499DE' }}
                        />
                      )}
                      <span>{truncate(rawContentToText(value)) || t('predefined-value-image-only')}</span>
                    </span>
                  ),
              }))}
            />
          </div>
          <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 8 }}>
            <Button size='small' type='primary' disabled={selected.length === 0} onClick={handleApplyText}>
              {t('apply')}
            </Button>
          </div>
        </>
      )}
    </div>
  )
}

const sectionStyle = {
  borderTop: '1px solid rgba(11, 34, 57, 0.12)',
  paddingTop: 8,
}

const thumbButtonStyle = {
  padding: 0,
  border: '1px solid rgba(11,34,57,0.12)',
  borderRadius: 6,
  background: '#fafafa',
  cursor: 'pointer',
  height: 64,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  overflow: 'hidden',
}

const thumbImgStyle = { maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }

export default TriggerResultPredefinedValues
